import React from 'react';
import styled from 'styled-components';

const MatchScoreButton = ({ onClick, hasScore = false, scoreStatus = null, seasonCompleted = false, isWindowLocked = false }) => {
    const getButtonText = () => {
        if (isWindowLocked && !hasScore) {
            return '🔒 Locked';
        }
        if (hasScore) {
            switch (scoreStatus) {
                case 'pending_verification':
                    return '📋 Verify Score';
                case 'pending_confirmation':
                    return '⏳ Pending Verification';
                case 'verified':
                    return '✅ View Score';
                case 'disputed':
                    return '⚠️ Score Disputed';
                default:
                    return '📊 Submit Score';
            }
        }
        return '📊 Submit Score';
    };

    const getButtonColor = () => {
        if (isWindowLocked && !hasScore) {
            return '#9ca3af'; // gray for locked
        }
        if (hasScore) {
            switch (scoreStatus) {
                case 'pending_verification':
                    return '#f59e0b'; // amber
                case 'pending_confirmation':
                    return '#6b7280'; // gray
                case 'verified':
                    return '#10b981'; // green
                case 'disputed':
                    return '#ef4444'; // red
                default:
                    return '#6b7280'; // gray
            }
        }
        return '#3b82f6'; // blue
    };

    // Determine if button should be disabled
    // Disable if:
    // 1. Window is locked AND no score exists yet (can't submit new scores)
    // 2. Season completed AND (no score OR pending verification)
    // Keep enabled for: verified (view), disputed (view), pending_confirmation (view)
    const isDisabled = (isWindowLocked && !hasScore) || (seasonCompleted && (
        !hasScore || // No score yet - can't submit new scores
        scoreStatus === 'pending_verification' // Can verify - but can't verify after season ends
        // Keep enabled for: verified (view), disputed (view), pending_confirmation (view)
    ));

    const getDisabledTitle = () => {
        if (isWindowLocked && !hasScore) {
            return 'Score submission will be available when this match window opens (28-day windows).';
        }
        if (!isDisabled) return '';
        if (!hasScore) return 'Score submission is not available. The season has ended.';
        if (scoreStatus === 'pending_verification') return 'Score verification is not available. The season has ended.';
        return '';
    };

    return (
        <ScoreButton 
            onClick={onClick} 
            $hasScore={hasScore}
            $color={getButtonColor()}
            disabled={isDisabled}
            title={getDisabledTitle()}
        >
            {getButtonText()}
        </ScoreButton>
    );
};

const ScoreButton = styled.button`
    background: ${props => props.$color};
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        filter: brightness(1.05);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }

    @media (max-width: 768px) {
        padding: 6px 12px;
        font-size: 11px;
        border-radius: 16px;
    }
`;

export default MatchScoreButton;
